import {
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Select,
  Textarea,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { EspdRequirementDto, EspdRequirementResponseDto } from "../../../../autogen/bff-api";
import { ESPD_COUNTRY_CODES, ESPD_CURRENCY_CODES, ESPD_UNIT_CODES } from "./espdCodeLists";

// Backend DTO uses String, not an enum, so the codegen emits `string`.
type EspdResponseDataType = string;

interface Props {
  requirement: EspdRequirementDto;
  value: string | boolean | number | null | undefined;
  response?: EspdRequirementResponseDto;
  onChange: (requirementId: string, value: string | boolean | number | null, instanceIndex?: number | null) => void;
  onMetadataChange?: (requirementId: string, field: string, value: string | null, instanceIndex?: number | null) => void;
  readOnly?: boolean;
  instanceIndex?: number | null;
  hasError?: boolean;
}

export const EspdRequirementField = ({ requirement, value, response, onChange, onMetadataChange, readOnly, instanceIndex, hasError }: Props) => {
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language === "no" || i18n.language === "nb";
  const label = isNorwegian && requirement.descriptionNorwegian ? requirement.descriptionNorwegian : requirement.description;

  const handleChange = (val: string | boolean | number | null) => {
    onChange(requirement.id, val, instanceIndex);
  };

  const handleMetadata = (field: string, val: string | null) => {
    onMetadataChange?.(requirement.id, field, val, instanceIndex);
  };

  return (
    <FormControl mb="3" isInvalid={hasError}>
      <FormLabel fontSize="sm" fontWeight={requirement.responseDataType === "INDICATOR" ? "medium" : "normal"}>
        {label}
        {requirement.optional && (
          <span style={{ fontStyle: "italic", marginLeft: 4 }}>({t("Optional")})</span>
        )}
      </FormLabel>
      {renderField(requirement.responseDataType, value, response, handleChange, handleMetadata, readOnly, t)}
    </FormControl>
  );
};

function renderField(
  dataType: EspdResponseDataType,
  value: string | boolean | number | null | undefined,
  response: EspdRequirementResponseDto | undefined,
  onChange: (val: string | boolean | number | null) => void,
  onMetadata: (field: string, val: string | null) => void,
  readOnly: boolean | undefined,
  t: (key: string) => string
) {
  switch (dataType) {
    case "INDICATOR":
      return (
        <RadioGroup
          value={value === true ? "yes" : value === false ? "no" : ""}
          onChange={(val) => onChange(val === "yes" ? true : false)}
          isDisabled={readOnly}
        >
          <HStack spacing="4">
            <Radio value="yes" colorScheme="teal">{t("Yes")}</Radio>
            <Radio value="no" colorScheme="teal">{t("No")}</Radio>
          </HStack>
        </RadioGroup>
      );
    case "DATE":
      return (
        <Input
          type="date"
          size="sm"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          isReadOnly={readOnly}
        />
      );
    case "DESCRIPTION":
      return (
        <Textarea
          size="sm"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={t("espd_enter_description_placeholder")}
          isReadOnly={readOnly}
          rows={3}
        />
      );
    case "AMOUNT":
      return (
        <HStack spacing="2" alignItems="flex-start">
          <NumberInput
            size="sm"
            flex="1"
            value={(value as number) ?? ""}
            onChange={(_, num) => onChange(isNaN(num) ? null : num)}
            isReadOnly={readOnly}
          >
            <NumberInputField placeholder={t("Amount")} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Select
            size="sm"
            w="140px"
            value={response?.currencyId ?? "NOK"}
            onChange={(e) => onMetadata("currencyId", e.target.value || null)}
            isDisabled={readOnly}
          >
            {ESPD_CURRENCY_CODES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </Select>
        </HStack>
      );
    case "CODE":
      return (
        <Input
          size="sm"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={t("Reference")}
          isReadOnly={readOnly}
        />
      );
    case "CODE_COUNTRY":
      return (
        <Select
          size="sm"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={t("Select country")}
          isDisabled={readOnly}
        >
          {ESPD_COUNTRY_CODES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name} ({c.code})
            </option>
          ))}
        </Select>
      );
    case "PERCENTAGE":
      return (
        <NumberInput
          size="sm"
          value={(value as number) ?? ""}
          onChange={(_, num) => onChange(isNaN(num) ? null : num)}
          min={0}
          max={100}
          isReadOnly={readOnly}
        >
          <NumberInputField placeholder="%" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      );
    case "PERIOD":
      return (
        <Input
          size="sm"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={t("Period")}
          isReadOnly={readOnly}
        />
      );
    case "QUANTITY_INTEGER":
      return (
        <NumberInput
          size="sm"
          value={(value as number) ?? ""}
          onChange={(_, num) => onChange(isNaN(num) ? null : Math.round(num))}
          precision={0}
          isReadOnly={readOnly}
        >
          <NumberInputField placeholder={t("Quantity")} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      );
    case "QUANTITY_YEAR":
      return (
        <NumberInput
          size="sm"
          value={(value as number) ?? ""}
          onChange={(_, num) => onChange(isNaN(num) ? null : Math.round(num))}
          min={1900}
          max={2100}
          precision={0}
          isReadOnly={readOnly}
        >
          <NumberInputField placeholder={t("Year")} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      );
    case "QUANTITY":
      return (
        <HStack spacing="2" alignItems="flex-start">
          <NumberInput
            size="sm"
            flex="1"
            value={(value as number) ?? ""}
            onChange={(_, num) => onChange(isNaN(num) ? null : num)}
            isReadOnly={readOnly}
          >
            <NumberInputField placeholder={t("Quantity")} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Select
            size="sm"
            w="180px"
            value={response?.quantityUnitCode ?? "C62"}
            onChange={(e) => onMetadata("quantityUnitCode", e.target.value || null)}
            isDisabled={readOnly}
          >
            {ESPD_UNIT_CODES.map((u) => (
              <option key={u.code} value={u.code}>
                {u.name}
              </option>
            ))}
          </Select>
        </HStack>
      );
    case "EVIDENCE_URL":
      return (
        <Input
          size="sm"
          type="url"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={t("https://...")}
          isReadOnly={readOnly}
        />
      );
    default:
      return (
        <Input
          size="sm"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          isReadOnly={readOnly}
        />
      );
  }
}
